import * as Yup from 'yup';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import { DatePicker } from '@mui/x-date-pickers';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button, InputAdornment, Link, MenuItem } from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useAppDispatcher } from 'src/redux/store';
import { createIssuedCheque, updateIssuedCheque } from 'src/redux/slices/issued-cheque';

import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';

import {
  IssuedChequeBank,
  IssuedChequeType,
  IIssuedChequeItem,
  ISSUED_CHEQUE_BANK_OPTIONS,
  ISSUED_CHEQUE_TYPE_OPTIONS,
} from 'src/types/issued-cheque';

type Props = {
  currentCheque?: IIssuedChequeItem;
};

const startOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const IssuedChequeSchema = Yup.object().shape({
  bank: Yup.mixed<IssuedChequeBank>()
    .oneOf(Object.values(IssuedChequeBank))
    .required('Bank is required'),
  chequeNumber: Yup.string().trim().required('Cheque number is required'),
  chequeDate: Yup.date().required('Cheque date is required'),
  amountInCents: Yup.number()
    .typeError('Amount must be a number')
    .moreThan(0, 'Amount must be more than 0')
    .required('Amount is required'),
  type: Yup.mixed<IssuedChequeType>()
    .oneOf(Object.values(IssuedChequeType))
    .required('Type is required'),
  payee: Yup.string().trim().required('Payee is required'),
  note: Yup.string().nullable().max(1000, 'Note must be at most 1000 characters'),
});

export default function IssuedChequeNewEditForm({ currentCheque }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const dispatch = useAppDispatcher();

  const defaultValues = useMemo(
    () => ({
      bank: currentCheque?.bank ?? IssuedChequeBank.BANK_OF_CEYLON,
      chequeNumber: currentCheque?.chequeNumber ?? '',
      chequeDate: currentCheque?.chequeDate ? new Date(currentCheque.chequeDate) : new Date(),
      amountInCents: currentCheque?.amountInCents ? currentCheque.amountInCents / 100 : 0,
      type: currentCheque?.type ?? IssuedChequeType.RAW_MATERIALS,
      payee: currentCheque?.payee ?? '',
      note: currentCheque?.note ?? '',
    }),
    [currentCheque]
  );

  const methods = useForm({
    resolver: yupResolver(IssuedChequeSchema),
    defaultValues,
    mode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    const cheque = {
      ...data,
      // Whole cents, avoiding floating point drift (e.g. 0.29 * 100).
      amountInCents: Math.round(data.amountInCents * 100),
      note: data.note || undefined,
    };

    try {
      if (currentCheque) {
        await dispatch(updateIssuedCheque({ id: currentCheque._id, cheque })).unwrap();
        enqueueSnackbar('Cheque updated successfully');
        navigate(paths.dashboard.issuedCheque.view(currentCheque._id));
      } else {
        await dispatch(createIssuedCheque(cheque)).unwrap();
        enqueueSnackbar('Cheque entered successfully');
        navigate(paths.dashboard.issuedCheque.root);
      }
    } catch (e) {
      enqueueSnackbar(typeof e === 'string' ? e : e?.message || 'Failed to save the cheque', {
        variant: 'error',
      });
    }
  });

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid container spacing={3}>
        <Grid xs={12}>
          <Card sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid xs={12} mt={2} mb={1}>
                <Typography variant="subtitle2">Cheque Information</Typography>
              </Grid>

              <Grid xs={12} md={6}>
                <RHFSelect required name="bank" label="Bank">
                  {ISSUED_CHEQUE_BANK_OPTIONS.map((bank) => (
                    <MenuItem key={bank} value={bank}>
                      {bank}
                    </MenuItem>
                  ))}
                </RHFSelect>
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField required name="chequeNumber" label="Cheque Number" />
              </Grid>

              <Grid xs={12} md={6}>
                <Controller
                  name="chequeDate"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <DatePicker
                      label="Cheque Date"
                      // Today or a future (post-dated) day.
                      minDate={currentCheque ? undefined : startOfToday()}
                      value={field.value}
                      onChange={(newValue) => field.onChange(newValue)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!error,
                          helperText: error?.message,
                          required: true,
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField
                  required
                  name="amountInCents"
                  label="Cheque Amount"
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">Rs</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField required name="payee" label="Payee" />
              </Grid>

              <Grid xs={12} md={6}>
                <RHFSelect required name="type" label="Type">
                  {ISSUED_CHEQUE_TYPE_OPTIONS.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </RHFSelect>
              </Grid>

              <Grid xs={12}>
                <RHFTextField
                  name="note"
                  label="Note"
                  multiline
                  rows={3}
                  placeholder="Any extra information about this cheque"
                />
              </Grid>
            </Grid>

            <Stack direction="row" justifyContent="flex-end" gap={2} mt={4}>
              <Link
                component={RouterLink}
                href={
                  currentCheque
                    ? paths.dashboard.issuedCheque.view(currentCheque._id)
                    : paths.dashboard.issuedCheque.root
                }
              >
                <Button variant="outlined" color="inherit">
                  Cancel
                </Button>
              </Link>

              <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                {currentCheque ? 'Save Changes' : 'Enter Cheque'}
              </LoadingButton>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
